import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Routes, Route, NavLink, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, BookText, FileText, Users2, Truck, Scale, ShieldCheck,
  Settings2, UsersRound, LogOut, Loader2, Building2, Landmark, ChevronLeft,
  ShoppingCart, ShoppingBag, Wallet, Percent, BarChart3, SlidersHorizontal,
  Boxes, Building, CheckSquare, CalendarClock, Table2, Menu, X,
} from "lucide-react";
import { ZatcaBanner } from "./components/ZatcaBanner";
import { finApi, setToken, getToken, apiErr } from "./api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

import FinDashboard from "./pages/Dashboard";
import FinAccounts from "./pages/Accounts";
import FinJournals from "./pages/Journals";
import FinAmericanJournal from "./pages/AmericanJournal";
import { FinCustomers, FinSuppliers } from "./pages/Parties";
import { FinTrialBalance, FinLedger, FinAudit } from "./pages/Reports";
import { FinUsers, FinCompany, FinSettings } from "./pages/Admin";
import { FinSales, FinPurchases } from "./pages/Trade";
import { FinPayments, FinVat, FinStatements } from "./pages/MoreFinance";
import FinAssets from "./pages/Assets";
import FinOpeningBalances from "./pages/OpeningBalances";
import FinPartyStatement from "./pages/PartyStatement";
import FinInventory from "./pages/Inventory";
import { FinApprovals, FinPeriods } from "./pages/Governance";

const Ctx = createContext(null);
export const useFin = () => useContext(Ctx);

export const NAV = [
  { to: "/finance", end: true, icon: LayoutDashboard, label: "لوحة التحكم", perm: "reports.view" },
  { to: "/finance/sales", icon: ShoppingCart, label: "المبيعات", perm: "customers.view" },
  { to: "/finance/purchases", icon: ShoppingBag, label: "المشتريات", perm: "suppliers.view" },
  { to: "/finance/payments", icon: Wallet, label: "المدفوعات", perm: "reports.view" },
  { to: "/finance/accounts", icon: BookText, label: "دليل الحسابات", perm: "accounts.view" },
  { to: "/finance/journals", icon: FileText, label: "القيود اليومية", perm: "journals.view" },
  { to: "/finance/opening-balances", icon: Scale, label: "الأرصدة الافتتاحية", perm: "journals.view" },
  { to: "/finance/american-journal", icon: Table2, label: "الدفتر اليومي الأمريكي", perm: "journals.view" },
  { to: "/finance/customers", icon: Users2, label: "العملاء", perm: "customers.view" },
  { to: "/finance/suppliers", icon: Truck, label: "الموردون", perm: "suppliers.view" },
  { to: "/finance/vat", icon: Percent, label: "الإقرار الضريبي", perm: "reports.view" },
  { to: "/finance/assets", icon: Building, label: "الأصول الثابتة", perm: "reports.view" },
  { to: "/finance/inventory", icon: Boxes, label: "المخزون", perm: "reports.view" },
  { to: "/finance/statements", icon: BarChart3, label: "القوائم المالية", perm: "reports.view" },
  { to: "/finance/approvals", icon: CheckSquare, label: "الموافقات", perm: "reports.view" },
  { to: "/finance/periods", icon: CalendarClock, label: "الفترات المالية", perm: "reports.view" },
  { to: "/finance/trial-balance", icon: Scale, label: "ميزان المراجعة", perm: "reports.view" },
  { to: "/finance/ledger", icon: Landmark, label: "دفتر الأستاذ", perm: "reports.view" },
  { to: "/finance/party-statement", icon: FileText, label: "كشف حساب", perm: "reports.view" },
  { to: "/finance/audit", icon: ShieldCheck, label: "سجل التدقيق", perm: "audit.view" },
  { to: "/finance/users", icon: UsersRound, label: "المستخدمون", perm: "users.view" },
  { to: "/finance/settings", icon: SlidersHorizontal, label: "الضريبة و ZATCA", perm: "companies.view" },
  { to: "/finance/company", icon: Settings2, label: "بيانات الشركة", perm: "companies.view" },
];

function Login({ onAuthed }) {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", company_name: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fn = mode === "login" ? finApi.login : finApi.register;
      const res = await fn(form);
      setToken(res.token);
      onAuthed(res.user);
      toast.success(mode === "login" ? "مرحباً بك" : "تم إنشاء الحساب والشركة");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div dir="rtl" className="min-h-screen bg-slate-950 flex items-center justify-center p-4 font-body">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 justify-center mb-8">
          <div className="h-12 w-12 rounded-2xl bg-emerald-500 flex items-center justify-center">
            <Landmark className="h-6 w-6 text-slate-950" />
          </div>
          <div className="text-right">
            <div className="text-white font-extrabold text-xl">برنامج WorkZone المحاسبي</div>
            <div className="text-emerald-400 text-xs">نظام محاسبي ومالي احترافي</div>
          </div>
        </div>
        <form onSubmit={submit} data-testid="fin-login-form"
          className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-4 shadow-2xl">
          <div className="flex rounded-xl bg-slate-800 p-1 text-sm">
            <button type="button" data-testid="fin-tab-login" onClick={() => setMode("login")}
              className={`flex-1 py-2 rounded-lg transition ${mode === "login" ? "bg-emerald-500 text-slate-950 font-bold" : "text-slate-300"}`}>تسجيل الدخول</button>
            <button type="button" data-testid="fin-tab-register" onClick={() => setMode("register")}
              className={`flex-1 py-2 rounded-lg transition ${mode === "register" ? "bg-emerald-500 text-slate-950 font-bold" : "text-slate-300"}`}>حساب جديد</button>
          </div>
          {mode === "register" && (
            <>
              <Field label="اسم الشركة"><Input data-testid="fin-company-name" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></Field>
              <Field label="اسمك"><Input data-testid="fin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></Field>
            </>
          )}
          <Field label="البريد الإلكتروني"><Input data-testid="fin-email" type="email" dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></Field>
          <Field label="كلمة المرور"><Input data-testid="fin-password" type="password" dir="ltr" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></Field>
          <Button type="submit" disabled={busy} data-testid="fin-submit"
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold h-11">
            {busy && <Loader2 className="h-4 w-4 animate-spin ml-2" />}
            {mode === "login" ? "دخول" : "إنشاء الحساب"}
          </Button>
          <a href="/" className="block text-center text-xs text-slate-500 hover:text-slate-300">العودة إلى WorkZone</a>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-slate-300 text-xs">{label}</Label>{children}</div>
);

function Sidebar({ items, onNav }) {
  const { user, logout } = useFin();
  return (
    <>
      <div className="p-5 border-b border-slate-800 flex items-center gap-2.5">
        <div className="h-9 w-9 rounded-xl bg-emerald-500 flex items-center justify-center"><Landmark className="h-5 w-5 text-slate-950" /></div>
        <div className="min-w-0"><div className="text-white font-extrabold text-sm">برنامج WorkZone المحاسبي</div>
          <div className="text-[11px] text-emerald-400 truncate max-w-[150px]">{user?.company?.name}</div></div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={onNav} data-testid={`fin-nav-${n.to.split("/").pop() || "dashboard"}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${isActive ? "bg-emerald-500 text-slate-950 font-bold" : "hover:bg-slate-800"}`}>
            <n.icon className="h-4.5 w-4.5" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-slate-800">
        <div className="px-3 py-2 text-xs">
          <div className="text-white font-semibold">{user?.name}</div>
          <div className="text-slate-500">{user?.role_label}</div>
        </div>
        <button onClick={logout} data-testid="fin-logout"
          className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-sm text-rose-300 hover:bg-slate-800">
          <LogOut className="h-4 w-4" /> تسجيل الخروج
        </button>
      </div>
    </>
  );
}

function Layout({ children }) {
  const { user, can } = useFin();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const items = NAV.filter((n) => can(n.perm));
  const path = loc.pathname.replace(/\/+$/, "") || "/";
  const current = items.find((n) => (n.end ? path === n.to : path.startsWith(n.to)))?.label;
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  return (
    <div dir="rtl" className="min-h-screen bg-slate-100 font-body lg:flex">
      <aside className="hidden lg:flex w-64 shrink-0 bg-slate-950 text-slate-300 flex-col fixed inset-y-0 right-0">
        <Sidebar items={items} />
      </aside>
      <header className="lg:hidden sticky top-0 z-30 flex items-center gap-3 bg-slate-950 px-4 py-3 text-white" data-testid="fin-mobile-header">
        <button onClick={() => setOpen(true)} className="rounded-lg p-1.5 hover:bg-slate-800" aria-label="القائمة" data-testid="fin-mobile-menu-btn"><Menu className="h-6 w-6" /></button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-extrabold">{current || "برنامج WorkZone المحاسبي"}</div>
          <div className="truncate text-[11px] text-emerald-400">{user?.company?.name}</div>
        </div>
        <div className="h-8 w-8 rounded-lg bg-emerald-500 flex items-center justify-center"><Landmark className="h-4 w-4 text-slate-950" /></div>
      </header>
      {open && (
        <div className="lg:hidden fixed inset-0 z-[70]" data-testid="fin-mobile-drawer">
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 right-0 flex w-72 max-w-[85vw] flex-col bg-slate-950 text-slate-300 shadow-2xl">
            <button onClick={() => setOpen(false)} className="absolute left-3 top-4 rounded-lg p-1.5 text-slate-400 hover:bg-slate-800" aria-label="إغلاق" data-testid="fin-mobile-close"><X className="h-5 w-5" /></button>
            <Sidebar items={items} onNav={() => setOpen(false)} />
          </aside>
        </div>
      )}
      <main className="fin-main flex-1 min-w-0 p-3 sm:p-4 lg:mr-64 lg:p-6">
        <ZatcaBanner />
        {children}
      </main>
    </div>
  );
}

export default function FinanceApp() {
  const [user, setUser] = useState(null); // null=loading, false=guest, obj=auth
  const navigate = useNavigate();

  useEffect(() => {
    if (!getToken()) { setUser(false); return; }
    finApi.me().then(setUser).catch(() => { setToken(""); setUser(false); });
  }, []);

  const logout = async () => {
    try { await finApi.logout(); } catch {}
    setToken(""); setUser(false);
    navigate("/finance");
  };

  const can = useMemo(() => (perm) => {
    const ps = user?.permissions || [];
    if (ps.includes("*")) return true;
    if (ps.includes(perm)) return true;
    const [mod, act] = perm.split(".");
    return ps.includes(`${mod}.*`) || ps.includes(`*.${act}`);
  }, [user]);

  if (user === null) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div>;
  if (!user) return <Login onAuthed={setUser} />;

  return (
    <Ctx.Provider value={{ user, setUser, logout, can }}>
      <Layout>
        <Routes>
          <Route path="" element={<FinDashboard />} />
          <Route path="sales" element={<FinSales />} />
          <Route path="purchases" element={<FinPurchases />} />
          <Route path="payments" element={<FinPayments />} />
          <Route path="accounts" element={<FinAccounts />} />
          <Route path="journals" element={<FinJournals />} />
          <Route path="opening-balances" element={<FinOpeningBalances />} />
          <Route path="american-journal" element={<FinAmericanJournal />} />
          <Route path="customers" element={<FinCustomers />} />
          <Route path="suppliers" element={<FinSuppliers />} />
          <Route path="vat" element={<FinVat />} />
          <Route path="assets" element={<FinAssets />} />
          <Route path="inventory" element={<FinInventory />} />
          <Route path="statements" element={<FinStatements />} />
          <Route path="approvals" element={<FinApprovals />} />
          <Route path="periods" element={<FinPeriods />} />
          <Route path="trial-balance" element={<FinTrialBalance />} />
          <Route path="ledger" element={<FinLedger />} />
          <Route path="party-statement" element={<FinPartyStatement />} />
          <Route path="audit" element={<FinAudit />} />
          <Route path="users" element={<FinUsers />} />
          <Route path="settings" element={<FinSettings />} />
          <Route path="company" element={<FinCompany />} />
        </Routes>
      </Layout>
    </Ctx.Provider>
  );
}

// Shared UI helpers for pages
export const PageHeader = ({ title, subtitle, action }) => (
  <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
    <div className="min-w-0"><h1 className="text-xl sm:text-2xl font-extrabold text-slate-900">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}</div>
    {action}
  </div>
);

export const Card = ({ children, className = "", ...rest }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm ${className}`} {...rest}>{children}</div>
);
